import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useStoreState } from 'easy-peasy';
import { Button, IconButton, Tooltip, Box, Grid } from '@material-ui/core';
import { ChevronLeftRounded, ArrowBack } from '@material-ui/icons';
import _e from 'common/localeUtil';
import TemplateRevisionStatusComponent from './TemplateRevisionStatusComponent';
import EditorLeftPanelSections from './EditorLeftPanelSections';
import EditorLeftPanelPreview from './EditorLeftPanelPreview';
import { EditorLeftPanelContext } from './EditorLeftPanelContext';
import { setContactFormToTemplate, setDefault2Widgets } from '../utils';
import WelcomeDialog from './dialog/WelcomeDialog';
import GMButton from './button/GMButton';
import {
	SHOWING_SECTION_CONTENT,
	SHOWING_SECTION_SEO,
	SHOWING_SECTION_SETTINGS,
	SHOWING_SECTION_STYLE,
} from '../constants';
import EditorLeftPanelURL from './EditorLeftPanelURL';
import EditorLeftPanelResetTemplate from './EditorLeftPanelResetTemplate';
import SEOSubForm from './form/SEOSubForm';
import EditorLeftPanelStyle from './EditorLeftPanelStyle';

const useStyles = makeStyles((theme) => ({
	leftPanelBottom: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
	},
	line: {
		border: '1px solid #365275',
		width: '100%',
	},
	titleEdit: {
		fontSize: '0.8rem',
	},
	buttonPanelStyle: {
		display: 'flex',
		justifyContent: 'space-between',
		flexDirection: 'row',
		alignItems: 'center',
	},
	iconButtonStyle: {
		justifyContent: 'center',
		fontSize: '1rem',
		color: theme.palette.background.default,
	},
	buttonStyle: {
		padding: 6,
		justifyContent: 'center',
		fontSize: '1rem',
		backgroundColor: '#365275',
		color: theme.palette.background.default,
		'&:hover': {
			backgroundColor: '#365275',
			opacity: 0.8,
		},
	},
	buttonWizardLabelStyle: {
		textTransform: 'initial',
		fontSize: '0.8rem',
		color: theme.palette.background.paper,
		textDecoration: 'underline',
	},
	buttonLabelStyle: {
		textTransform: 'initial',
		justifyContent: 'center',
	},
	drawerIconButtonStyle: {
		color: theme.palette.background.paper,
	},
}));

const EditorLeftPanelWebsite = (props) => {
	const classes = useStyles();
	const contactForm = useStoreState((state) => state.template.contactForm);
	const subdomain = useStoreState((state) => state.template.subdomain);

	const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
	const [showingContent, setShowingContent] = useState(SHOWING_SECTION_CONTENT);
	const [showingStyle, setShowingStyle] = useState(SHOWING_SECTION_STYLE);
	const [showingSettings, setShowingSettings] = useState(SHOWING_SECTION_SETTINGS);
	const [showingSEO, setShowingSEO] = useState(SHOWING_SECTION_SEO);
	const doRedoAction = () => {
		window.gm_track(`Redo last action clicked`);

		window.grapesjs.runCommand('core:redo');
	};

	const doUndoAction = () => {
		window.gm_track(`Undo last action clicked`);

		window.grapesjs.runCommand('core:undo');
	};

	const onClickMenu = (section) => () => {
		window.gm_track(`Left Menu clicked`, { section });

		if (section === 'content') {
			setShowingContent(true);
			setShowingSEO(false);
			setShowingStyle(false);
			setShowingSettings(false);
		} else if (section === 'style') {
			setShowingStyle(true);
			setShowingSEO(false);
			setShowingContent(false);
			setShowingSettings(false);
		} else if (section === 'seo') {
			setShowingSEO(true);
			setShowingContent(false);
			setShowingStyle(false);
			setShowingSettings(false);
		} else if (section === 'settings') {
			setShowingSettings(true);
			setShowingSEO(false);
			setShowingContent(false);
			setShowingStyle(false);
		}
	};

	const handleClickBack = () => {
		setShowingContent(true);
		setShowingStyle(false);
		setShowingSEO(false);
		setShowingSettings(false);
	};

	const toggleShowWelcomeDialog = () => {
		window.gm_track(`Get Started button clicked`);

		setShowWelcomeDialog(!showWelcomeDialog);
	};

	useEffect(() => {
		// Load contact form to right
		setContactFormToTemplate(contactForm);
		// Set default 2 widgets to right
		setDefault2Widgets();
		if (!subdomain) {
			toggleShowWelcomeDialog();
		}
	}, []);

	const fullScreen = () => {
		console.log('fullScreen');
		window.grapesjs.runCommand('core:preview');
	};

	const additionClass = 'insideMode';
	const { values, submitForm, toggleDrawer, setFieldValue } = props;

	const renderSettingsActions = () => {
		return (
			<Grid container spacing={1}>
				<Grid item xs={12} lg={4}>
					<Button
						fullWidth
						variant="contained"
						onClick={onClickMenu('style')}
						classes={{
							root: classes.buttonStyle,
							label: classes.buttonLabelStyle,
						}}
					>
						<svg style={{ width: 20, height: 20, paddingRight: 5 }} viewBox="0 0 24 24">
							<path
								fill="#FFFFFF"
								d="M12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2C17.5,2 22,6 22,11A6,6 0 0,1 16,17H14.2C13.9,17 13.7,17.2 13.7,17.5C13.7,17.6 13.8,17.7 13.8,17.8C14.2,18.3 14.4,18.9 14.4,19.5C14.5,20.9 13.4,22 12,22M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C12.3,20 12.5,19.8 12.5,19.5C12.5,19.3 12.4,19.2 12.4,19.1C12,18.6 11.8,18.1 11.8,17.5C11.8,16.1 12.9,15 14.3,15H16A4,4 0 0,0 20,11C20,7.1 16.4,4 12,4M6.5,10C7.3,10 8,10.7 8,11.5C8,12.3 7.3,13 6.5,13C5.7,13 5,12.3 5,11.5C5,10.7 5.7,10 6.5,10M9.5,6C10.3,6 11,6.7 11,7.5C11,8.3 10.3,9 9.5,9C8.7,9 8,8.3 8,7.5C8,6.7 8.7,6 9.5,6M14.5,6C15.3,6 16,6.7 16,7.5C16,8.3 15.3,9 14.5,9C13.7,9 13,8.3 13,7.5C13,6.7 13.7,6 14.5,6M17.5,10C18.3,10 19,10.7 19,11.5C19,12.3 18.3,13 17.5,13C16.7,13 16,12.3 16,11.5C16,10.7 16.7,10 17.5,10Z"
							/>
						</svg>
						{_e('styles')}
					</Button>
				</Grid>
				<Grid item xs={12} lg={4}>
					<Button
						fullWidth
						variant="contained"
						onClick={onClickMenu('seo')}
						classes={{
							root: classes.buttonStyle,
							label: classes.buttonLabelStyle,
						}}
					>
						<svg style={{ width: 20, height: 20, paddingRight: 5 }} viewBox="0 0 24 24">
							<path
								fill="#FFFFFF"
								d="M16.36,14C16.44,13.34 16.5,12.68 16.5,12C16.5,11.32 16.44,10.66 16.36,10H19.74C19.9,10.64 20,11.31 20,12C20,12.69 19.9,13.36 19.74,14M14.59,19.56C15.19,18.45 15.65,17.25 15.97,16H18.92C17.96,17.65 16.43,18.93 14.59,19.56M14.34,14H9.66C9.56,13.34 9.5,12.68 9.5,12C9.5,11.32 9.56,10.65 9.66,10H14.34C14.43,10.65 14.5,11.32 14.5,12C14.5,12.68 14.43,13.34 14.34,14M12,19.96C11.17,18.76 10.5,17.43 10.09,16H13.91C13.5,17.43 12.83,18.76 12,19.96M8,8H5.08C6.03,6.34 7.57,5.06 9.4,4.44C8.8,5.55 8.35,6.75 8,8M5.08,16H8C8.35,17.25 8.8,18.45 9.4,19.56C7.57,18.93 6.03,17.65 5.08,16M4.26,14C4.1,13.36 4,12.69 4,12C4,11.31 4.1,10.64 4.26,10H7.64C7.56,10.66 7.5,11.32 7.5,12C7.5,12.68 7.56,13.34 7.64,14M12,4.03C12.83,5.23 13.5,6.57 13.91,8H10.09C10.5,6.57 11.17,5.23 12,4.03M18.92,8H15.97C15.65,6.75 15.19,5.55 14.59,4.44C16.43,5.07 17.96,6.34 18.92,8M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
							/>
						</svg>
						SEO
					</Button>
				</Grid>
				<Grid item xs={12} lg={4}>
					<Button
						fullWidth
						variant="contained"
						onClick={onClickMenu('settings')}
						classes={{
							root: classes.buttonStyle,
							label: classes.buttonLabelStyle,
						}}
					>
						<svg style={{ width: 20, height: 20, paddingRight: 5 }} viewBox="0 0 24 24">
							<path
								fill="currentColor"
								d="M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M12,10A2,2 0 0,0 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12A2,2 0 0,0 12,10M10,22C9.75,22 9.54,21.82 9.5,21.58L9.13,18.93C8.5,18.68 7.96,18.34 7.44,17.94L4.95,18.95C4.73,19.03 4.46,18.95 4.34,18.73L2.34,15.27C2.21,15.05 2.27,14.78 2.46,14.63L4.57,12.97L4.5,12L4.57,11L2.46,9.37C2.27,9.22 2.21,8.95 2.34,8.73L4.34,5.27C4.46,5.05 4.73,4.96 4.95,5.05L7.44,6.05C7.96,5.66 8.5,5.32 9.13,5.07L9.5,2.42C9.54,2.18 9.75,2 10,2H14C14.25,2 14.46,2.18 14.5,2.42L14.87,5.07C15.5,5.32 16.04,5.66 16.56,6.05L19.05,5.05C19.27,4.96 19.54,5.05 19.66,5.27L21.66,8.73C21.79,8.95 21.73,9.22 21.54,9.37L19.43,11L19.5,12L19.43,13L21.54,14.63C21.73,14.78 21.79,15.05 21.66,15.27L19.66,18.73C19.54,18.95 19.27,19.04 19.05,18.95L16.56,17.95C16.04,18.34 15.5,18.68 14.87,18.93L14.5,21.58C14.46,21.82 14.25,22 14,22H10M11.25,4L10.88,6.61C9.68,6.86 8.62,7.5 7.85,8.39L5.44,7.35L4.69,8.65L6.8,10.2C6.4,11.37 6.4,12.64 6.8,13.8L4.68,15.36L5.43,16.66L7.86,15.62C8.63,16.5 9.68,17.14 10.87,17.38L11.24,20H12.76L13.13,17.39C14.32,17.14 15.37,16.5 16.14,15.62L18.57,16.66L19.32,15.36L17.2,13.81C17.6,12.64 17.6,11.37 17.2,10.2L19.31,8.65L18.56,7.35L16.15,8.39C15.38,7.5 14.32,6.86 13.12,6.62L12.75,4H11.25Z"
							/>
						</svg>
						{_e('settings')}
					</Button>
				</Grid>
			</Grid>
		);
	};

	const renderActions = () => {
		return (
			<Box px={1} className={`leftpanel-content`}>
				<div className={classes.buttonPanelStyle}>
					<Button
						variant="text"
						classes={{
							label: classes.buttonWizardLabelStyle,
						}}
						onClick={toggleShowWelcomeDialog}
					>
						{_e('get_started')}
					</Button>
					<div className="right">
						<Tooltip title={_e('undo_last_action')}>
							<IconButton
								onClick={doUndoAction}
								classes={{
									root: classes.iconButtonStyle,
									label: classes.buttonLabelStyle,
								}}
							>
								<svg style={{ width: 15, height: 15 }} viewBox="0 0 24 24">
									<path
										fill="#f2f2f2"
										d="M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z"
									/>
								</svg>
							</IconButton>
						</Tooltip>
						<Tooltip title={_e('redo_last_action')}>
							<IconButton
								onClick={doRedoAction}
								classes={{
									root: classes.iconButtonStyle,
									label: classes.buttonLabelStyle,
								}}
							>
								<svg style={{ width: 15, height: 15 }} viewBox="0 0 24 24">
									<path
										fill="currentColor"
										d="M18.4,10.6C16.55,9 14.15,8 11.5,8C6.85,8 2.92,11.03 1.54,15.22L3.9,16C4.95,12.81 7.95,10.5 11.5,10.5C13.45,10.5 15.23,11.22 16.62,12.38L13,16H22V7L18.4,10.6Z"
									/>
								</svg>
							</IconButton>
						</Tooltip>
					</div>
				</div>
				{!showingContent && (
					<div className={`back-button`}>
						<GMButton Icon={ArrowBack} title={_e('back')} onClick={handleClickBack} />
					</div>
				)}
				{showingContent && renderSettingsActions()}
				{showingStyle && <EditorLeftPanelStyle {...props} subtitle={_e('styles_desc')} />}
				{showingSEO && <SEOSubForm />}
				{showingContent && <EditorLeftPanelSections />}
				{showingSettings && <EditorLeftPanelResetTemplate />}
			</Box>
		);
	};

	return (
		<EditorLeftPanelContext.Provider
			value={{
				clickFullScreen: fullScreen,
				formik: {
					values,
					submitForm,
					setFieldValue,
				},
			}}
		>
			<div
				className={`left-panel left-panel-wrapper ${additionClass} ${
					window.previewMode && 'preview-mode'
				}`}
			>
				<Box className="gm-logo-wrapper" px={1}>
					<Box display="flex" flexDirection="row">
						<Box flex={1}>
							<img
								style={{ marginBottom: 0 }}
								alt="GoalsMapper - Redefining Financial Planning"
								className="gm-logo"
								src="https://static.goalsmapper.com/public/platform/goalsmapper/gm-brand-white-rectangle.png"
							/>
							<div className={`title-leftpanel`}>
								{_e('personal_website_builder')}
							</div>
						</Box>
						<IconButton
							onClick={toggleDrawer}
							classes={{ root: classes.drawerIconButtonStyle }}
						>
							<ChevronLeftRounded />
						</IconButton>
					</Box>
					<EditorLeftPanelURL subdomain={subdomain || values?.subdomain} />
				</Box>
				<Box className={`leftpanel-content-bottom`} px={1} mx={0}>
					{renderActions()}
					<Box pv={1}>
						<div className={classes.line} />
						<EditorLeftPanelPreview previewButtonTitle={_e('preview')} />
						<div className={classes.line} />
						<TemplateRevisionStatusComponent />
					</Box>
				</Box>
				{showWelcomeDialog && (
					<WelcomeDialog
						open={showWelcomeDialog}
						handleClose={toggleShowWelcomeDialog}
						{...props}
					/>
				)}
			</div>
		</EditorLeftPanelContext.Provider>
	);
};

EditorLeftPanelWebsite.propTypes = {
	values: PropTypes.object,
	toggleDrawer: PropTypes.func,
	submitForm: PropTypes.func,
	setFieldValue: PropTypes.func,
};

export default EditorLeftPanelWebsite;
